import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { RecaptchaModule } from 'ng-recaptcha';
import { DirectivesModule } from 'src/app/core/directives/directives.module';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { getNetworkLogo } from 'src/app/core/utils/network-logo.util';
import { environment } from 'src/environments/environment';
import { buildSignupV2Theme } from './signup-v2-theme';

interface FeatureCard { title: string; text: string; icon: string; link: string; }

// Stroke icon paths (24x24) for the form fields.
const SIGNUP_ICONS = {
  user: 'M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10',
  mail: 'M4 6h16v12H4zM4 7l8 6 8-6',
  lock: 'M6 11h12v10H6zM8 11V8a4 4 0 0 1 8 0v3',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 8.5h.01',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  eyeOff: 'M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.2A10 10 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3.2 3.9M6.6 6.6A17 17 0 0 0 2 12s4 7 10 7a9.6 9.6 0 0 0 4.3-1',
  check: 'M5 12l5 5 9-10',
  dot: 'M12 12h.01',
};

/**
 * New-design sign-up page (ENABLE_NEW_SIGNUP). Presentation only: the form, validation, captcha,
 * terms dialog and sign-up call all stay in ConsumerSignupComponent, which passes them in here.
 */
@Component({
  selector: 'app-consumer-signup-v2',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, RecaptchaModule, DirectivesModule],
  templateUrl: './consumer-signup-v2.component.html',
  styleUrls: ['./consumer-signup-v2.component.scss', './signup-v2-form.scss', './signup-v2-inputs.scss'],
})
export class ConsumerSignupV2Component implements OnChanges {
  @Input() form: FormGroup;
  @Input() settingsData: any = {};
  @Input() captchaKey: string;
  @Input() isAgrees = false;
  @Input() channelName: string;
  @Output() submitted = new EventEmitter<void>();
  @Output() signIn = new EventEmitter<void>();
  @Output() agreeChange = new EventEmitter<boolean>();
  @Output() captchaResolved = new EventEmitter<string>();
  @Output() passwordInput = new EventEmitter<Event>();

  theme: { [key: string]: string } = {};
  showPassword = false;
  showConfirm = false;
  readonly icons = SIGNUP_ICONS;

  constructor(private commanSubscribeService: CommanSubscribeService, private utilityService: UtilityService) {}

  ngOnChanges() {
    this.theme = buildSignupV2Theme(this.settingsData);
  }

  private get performerData(): any {
    return this.commanSubscribeService.perfomerInfoData?.data?.Data?.[0] || {};
  }

  get networkLogo(): string {
    return getNetworkLogo(this.commanSubscribeService.perfomerInfoData, this.settingsData);
  }

  get networkName(): string {
    return this.settingsData?.channelInfo?.channel_display_name || this.performerData.channel_display_name || '';
  }

  // Each card links to the same page the header menu opens; Community only when the header shows it.
  get features(): FeatureCard[] {
    const base = this.commanSubscribeService.getChannelBasePath(this.channelName);
    const cards: FeatureCard[] = [
      { title: 'Live & upcoming events', text: 'Watch live and see what is coming up.', icon: 'M5 4.5v15l13-7.5z', link: `${base}/event` },
      { title: 'Videos on demand', text: 'Replays, highlights and more, any time.', icon: 'M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 8v4l3 2', link: base },
      { title: 'Official store', text: 'Exclusive merchandise and more.', icon: 'M6 7h12l-1 13H7zM9 7a3 3 0 0 1 6 0', link: `${base}/merchandise/merch-list` },
    ];
    if (environment.enable_community && this.utilityService.isActiveFeature(this.commanSubscribeService.featureInfo, 'community')) {
      cards.push({ title: 'Community', text: 'Connect and chat with other fans.', icon: 'M4 5h16v11H8l-4 4zM8 9h8M8 12h5', link: `${base}/community` });
    }
    return cards;
  }

  // Display only - mirrors ConsumerSignupComponent.checkStrength(), which still does the validation.
  get passwordRules(): { label: string; met: boolean }[] {
    const p: string = this.form?.get('password')?.value || '';
    return [
      { label: '8+ characters', met: p.length >= 8 },
      { label: 'Upper case', met: /[A-Z]/.test(p) },
      { label: 'Lower case', met: /[a-z]/.test(p) },
      { label: 'Number', met: /[0-9]/.test(p) },
      { label: 'Special character', met: /[$-/:-?{-~!"^_@`\[\]]/.test(p) },
    ];
  }

  showError(name: string): boolean {
    const control = this.form?.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  hasError(name: string, error: string): boolean {
    return !!this.form?.get(name)?.hasError(error);
  }

  trackByLink(_: number, card: FeatureCard): string {
    return card.link;
  }
}
